import React from 'react';
import classNames from 'classnames';
import { graphql, Link, useStaticQuery } from 'gatsby';

import { Consulting } from '@layouts/Consulting';
import {
	Awards,
	DescriptionWithImage,
	InternalLinking,
	Reviews,
} from '@components';
import {
	AssetTracking,
	Header,
	HowToMeasurePeople,
	OurProducts,
	SparkCaseStudy,
} from './components';

import FootFallAnalyticsImage from '@static/images/figures/footfall-analytics.webp';
import FootFallCaseStudyImage from '@static/images/figures/footfall-case-study.webp';
import ArrowRight from '@images/icons/arrow-right.svg';
import {
	footFallFeatures,
	internalLinkingData,
} from '@store/LocationAssetsTrackingData';
import { PageUrls } from '@utils/constants/PageUrls';
import useReviewsQuery from '@utils/hooks/useReviewsQuery';

import * as styles from './LocationAssetsTracking.module.scss';

export const LocationAssetsTracking: React.FC = () => {
	const { directus } = useStaticQuery(graphql`
		query LocationAndAssetsReviews {
			directus {
				PageReviews(filter: { pageName: { _contains: "location-assets" } }) {
					Reviews {
						Reviews_id {
							quote
							reviewerName
							reviewerPosition
							link
							title
							logo {
								id
								imageFile {
									extension
									publicURL
									childImageSharp {
										gatsbyImageData
									}
								}
							}
						}
					}
				}
			}
		}
	`);
	const reviews = useReviewsQuery(directus);

	return (
		<div className="z-99 relative overflow-hidden">
			<Header />
			<DescriptionWithImage
				className="gray-bg"
				imageOnRight
				imageClassName={styles.image}
				image={FootFallAnalyticsImage}
				disableContentPadding
			>
				<h2 className="font-size-medium-title mb-3">
					Custom Footfall Analytics
				</h2>
				<p className="font-size-sm mb-3">
					EMBIQ delivers solutions for footfall analytics based on customer
					ideas. The tool could be customized and used in the assessment of
					physical systems to measure the time needed to move from one point to
					another.
				</p>
				<ul className={styles.list}>
					<p className="font-size-md mb-2 font-400">Can be applied for:</p>

					{footFallFeatures.map((feature) => (
						<li
							key={feature.title}
							className={classNames(styles.listItem, 'mb-2')}
						>
							<div className="d-flex align-items-center">
								<img src={ArrowRight} alt="Arrow right" />
								<span className="font-600 font-size-sm">{feature.title}</span>
							</div>
							<p className="font-size-sm">{feature.description}</p>
						</li>
					))}
				</ul>
			</DescriptionWithImage>
			<HowToMeasurePeople />
			<DescriptionWithImage
				className={classNames(styles.typography, 'gray-bg')}
				imageClassName={styles.image}
				image={FootFallCaseStudyImage}
			>
				<p className="font-size-small-title mb-3 font-600">
					Footfall analytics in transportation
				</p>
				<p className="font-size-sm mb-4">
					The created platform uses tools such as DSDi Mobile, DSDi Manager and
					DSDi User to assist installation professionals and help them stay in
					control over service works.
				</p>
				<Link
					to={PageUrls.FOOTFALL_CASE_STUDY}
					className="bttn bttn-orange-border link"
				>
					See full case study
				</Link>
			</DescriptionWithImage>
			<AssetTracking />
			<SparkCaseStudy />
			<OurProducts />
			<InternalLinking className="gray-bg" links={internalLinkingData} />
			{!!reviews.length && (
				<Reviews
					title="Our client says"
					reviews={reviews}
					maxWidth="1300px"
					bg="white"
					titleClass="font-size-medium"
				/>
			)}
			<Awards className="gray-bg" />
			<Consulting />
		</div>
	);
};
